/**
 * Renders a styled circular dish marker (image + colored border + tail pointer)
 * to a data URL suitable for use as a Capacitor Google Maps `iconUrl`.
 *
 * Falls back to a solid-colored circle when the image cannot be loaded
 * (missing URL, network failure, or canvas tainted by a non-CORS image).
 */

const SIZE = 56; // image circle diameter (logical px)
const BORDER = 3;
const TAIL_HEIGHT = 9;
const TAIL_HALF_WIDTH = 6;
const DPR = 2; // render at 2x for retina

// Count-badge metrics. Only allocated when an icon is rendered with count >= 2.
const BADGE_R = 11;
const BADGE_RING = 2;
const BADGE_PAD = BADGE_R + BADGE_RING + 1; // overhang above + right of the image ring

const FALLBACK_BORDER = '#8f4420';
const FALLBACK_PLACEHOLDER_FILL = '#e5e7eb';

export type DishMarkerIcon = {
  dataUrl: string;
  width: number;
  height: number;
  anchorX: number;
  anchorY: number;
};

function getBrandColor(): string {
  if (typeof window === 'undefined') return FALLBACK_BORDER;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-primary')
    .trim();
  return v || FALLBACK_BORDER;
}

function getPlaceholderFill(): string {
  if (typeof window === 'undefined') return FALLBACK_PLACEHOLDER_FILL;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-light')
    .trim();
  return v || FALLBACK_PLACEHOLDER_FILL;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load ${src}`));
    img.src = src;
  });
}

function drawCountBadge(
  ctx: CanvasRenderingContext2D,
  imageCX: number,
  imageCY: number,
  imageOuterR: number,
  count: number,
  badgeColor: string,
): void {
  // 45° NE from the image center, badge centered on the outer ring edge so
  // it visually "sticks" to the marker without floating in space.
  const offset = imageOuterR * 0.707;
  const bcx = imageCX + offset;
  const bcy = imageCY - offset;

  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(bcx, bcy, BADGE_R + BADGE_RING, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = badgeColor;
  ctx.beginPath();
  ctx.arc(bcx, bcy, BADGE_R, 0, Math.PI * 2);
  ctx.fill();

  const text = count > 99 ? '99+' : String(count);
  ctx.fillStyle = '#fff';
  ctx.font = `bold ${count > 99 ? 9 : 13}px system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, bcx, bcy);
}

export async function buildDishMarkerIcon(
  imageUrl: string | null,
  options?: { borderColor?: string; count?: number },
): Promise<DishMarkerIcon> {
  const borderColor = options?.borderColor ?? getBrandColor();
  const placeholderFill = getPlaceholderFill();
  const count = options?.count;
  const showBadge = count != null && count >= 2;

  // Stacked icons need overhang above + right of the image ring for the
  // count badge. Single icons keep the original tight bounds so existing
  // markers don't shift on the map.
  const padTop = showBadge ? BADGE_PAD : 0;
  const padX = showBadge ? BADGE_PAD : 0;
  const TOTAL_W = SIZE + BORDER * 2 + padX * 2;
  const TOTAL_H = SIZE + BORDER * 2 + TAIL_HEIGHT + padTop;

  const canvas = document.createElement('canvas');
  canvas.width = TOTAL_W * DPR;
  canvas.height = TOTAL_H * DPR;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(DPR, DPR);

  // Tail (triangle) drawn first so the circle sits cleanly above it.
  const cx = TOTAL_W / 2;
  const circleCY = padTop + (SIZE + BORDER * 2) / 2;
  const outerR = SIZE / 2 + BORDER;

  ctx.fillStyle = borderColor;
  ctx.beginPath();
  ctx.moveTo(cx - TAIL_HALF_WIDTH, TOTAL_H - TAIL_HEIGHT);
  ctx.lineTo(cx + TAIL_HALF_WIDTH, TOTAL_H - TAIL_HEIGHT);
  ctx.lineTo(cx, TOTAL_H);
  ctx.closePath();
  ctx.fill();

  // Outer ring (border).
  ctx.beginPath();
  ctx.arc(cx, circleCY, outerR, 0, Math.PI * 2);
  ctx.fill();

  // Solid backdrop circle so transparent pixels in the image blend with
  // the app's surface color rather than the map background.
  ctx.fillStyle = placeholderFill;
  ctx.beginPath();
  ctx.arc(cx, circleCY, SIZE / 2, 0, Math.PI * 2);
  ctx.fill();

  // Inner image (circular clip).
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, circleCY, SIZE / 2, 0, Math.PI * 2);
  ctx.clip();

  if (imageUrl) {
    try {
      const img = await loadImage(imageUrl);
      // object-fit: cover — crop the longer axis from natural dimensions.
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      const square = Math.min(nw, nh);
      const sx = (nw - square) / 2;
      const sy = (nh - square) / 2;
      ctx.drawImage(img, sx, sy, square, square, cx - SIZE / 2, circleCY - SIZE / 2, SIZE, SIZE);
    } catch {
      /* backdrop already painted */
    }
  }

  ctx.restore();

  if (showBadge) {
    drawCountBadge(ctx, cx, circleCY, outerR, count!, borderColor);
  }

  let dataUrl: string;
  try {
    dataUrl = canvas.toDataURL('image/png');
  } catch {
    // Canvas tainted (CORS) — redraw placeholder-only and export.
    ctx.clearRect(0, 0, TOTAL_W, TOTAL_H);
    ctx.fillStyle = borderColor;
    ctx.beginPath();
    ctx.moveTo(cx - TAIL_HALF_WIDTH, TOTAL_H - TAIL_HEIGHT);
    ctx.lineTo(cx + TAIL_HALF_WIDTH, TOTAL_H - TAIL_HEIGHT);
    ctx.lineTo(cx, TOTAL_H);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, circleCY, outerR, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = placeholderFill;
    ctx.beginPath();
    ctx.arc(cx, circleCY, SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
    if (showBadge) {
      drawCountBadge(ctx, cx, circleCY, outerR, count!, borderColor);
    }
    dataUrl = canvas.toDataURL('image/png');
  }

  return {
    dataUrl,
    width: TOTAL_W,
    height: TOTAL_H,
    anchorX: cx,
    anchorY: TOTAL_H,
  };
}
